import { createContext, useContext, useEffect, useMemo, useRef, useState } from 'react';
import type { Locale } from '@/strings/types';

/**
 * Tab 5 — the Template Library (/library?tab=in_app), and the scripted sequence
 * that plays on it.
 *
 * WHAT THE REAL PAGE DOES (MoMP frontend/src/pages/TemplateLibrary.tsx and
 * components/CreateUserTemplateModal.tsx, read 2026-09-07):
 *   - three tabs: Catalog Items (card grid + search + multi-select category
 *     chips), My Templates (a table capped at N/30), In-App Templates (cards
 *     badged USER or SYSTEM, with a detail rail).
 *   - "+ New Template" opens a composer dialog: the catalog on the left, name
 *     and an ordered section list on the right, Cancel / Save & Publish.
 *   - a template is an ORDERED list of catalog sections, and each section
 *     carries a display format chosen from that catalog item's OWN allowlist:
 *     Action Items permits bullet | numbered | table, Decisions Made only
 *     bullet | numbered, and the system-instructions item permits none, which
 *     is why the app renders it as italic "renderer default".
 *
 * THE SEQUENCE IS THREE BEATS, and the first one exists because the owner asked
 * for it (2026-09-07): the composer "is really nice but needs a step behind it
 * so the user understands that there are templates already and they can create
 * more". So: browse a stocked library, open the composer, and watch the new
 * template land back in that same library as a card. The empty ninth cell in
 * the grid is deliberate — the new card fills it rather than reflowing the
 * eight that were already there.
 *
 * ⚠️ AR COPY IS A PROPOSAL PENDING OWNER SIGN-OFF (style guide workflow, step 7).
 * Note that the CATEGORY names are the part with teeth: a category on a public
 * page is a claim about what the product covers, and now it is that claim in two
 * languages.
 *
 * ⚠️ CONTENT POLICY: every template and catalog name below is invented except
 * "Detailed Meeting Minutes" and "Executive Summary Minutes", which are product
 * content the app itself ships. The owner's reference screenshots carry a real
 * account name and address, a live project code and real meeting content; only
 * the SHAPE was taken.
 *
 * ⚠️ AHEAD OF THE SHIPPED UI, deliberately (owner, 2026-09-07). The composer's
 * catalog rail today is a flat checkbox list with no categories; category chips
 * exist only on the Catalog Items tab. The owner confirmed categories are
 * coming and named Meetings, Lectures and Sales. Interviews and Projects are
 * proposals — a category name here is a public claim about what the product
 * covers, so cut them if the taxonomy lands differently.
 */

/* The app's display formats. KEYS — the visible word is looked up per locale in
   LibraryView, because this value is also what the composer prints in each
   section's format chip. */
export type Format = 'paragraph' | 'bullet' | 'numbered' | 'table';

/**
 * A catalog item that a template can carry, identified independently of what it
 * is CALLED.
 *
 * The composer ticks a catalog row as its section lands, and it used to do that
 * by comparing display strings — `SECTIONS.map(s => s.name)` against the rail's
 * item names. That works only while both sides are written in one language: two
 * translations that disagree by a single word would have left the rail
 * un-ticked through the entire compose beat, with nothing failing loudly.
 */
export type SectionKey =
  | 'title'
  | 'date'
  | 'attendees'
  | 'exec'
  | 'key'
  | 'decisions'
  | 'actions'
  | 'next';

export interface CatalogItem {
  /** Set only on the items this template actually picks; the rest never tick. */
  key?: SectionKey;
  name: string;
}

export interface CatalogCategory {
  name: string;
  /** How many items the category holds in FULL. The rail renders a slice. */
  count: number;
  /** The slice the rail renders, top to bottom. */
  items: readonly CatalogItem[];
}

/* Four categories are rendered and two are summarised, which is the whole
   answer to "50+ items without overwhelming": the visitor reads a count, not a
   list. Twelve item names on screen out of fifty-two is enough to show what a
   section IS; the rest is arithmetic they can do at a glance.

   Essentials and Meetings carry every section the template uses, so each pick
   ticks a row the visitor can actually see. Lectures and Sales stay untouched
   on purpose — this is a meetings template, and a catalog that reaches past the
   template being built is exactly the point. */
export const CATALOG: Record<Locale, readonly CatalogCategory[]> = {
  en: [
    {
      name: 'Essentials',
      count: 6,
      items: [
        { key: 'title', name: 'Meeting Title' },
        { key: 'date', name: 'Meeting Date' },
        { key: 'attendees', name: 'Attendees' },
      ],
    },
    {
      name: 'Meetings',
      count: 18,
      items: [
        { key: 'exec', name: 'Executive Summary' },
        { key: 'key', name: 'Key Points' },
        { key: 'decisions', name: 'Decisions Made' },
        { key: 'actions', name: 'Action Items' },
        { key: 'next', name: 'Next Steps' },
      ],
    },
    {
      name: 'Lectures',
      count: 9,
      items: [{ name: 'Learning Objectives' }, { name: 'Key Concepts' }],
    },
    {
      name: 'Sales',
      count: 11,
      items: [{ name: 'Pain Points' }, { name: 'Objections' }],
    },
  ],
  ar: [
    {
      name: 'الأساسيات',
      count: 6,
      items: [
        { key: 'title', name: 'عنوان الاجتماع' },
        { key: 'date', name: 'تاريخ الاجتماع' },
        { key: 'attendees', name: 'الحضور' },
      ],
    },
    {
      name: 'الاجتماعات',
      count: 18,
      items: [
        { key: 'exec', name: 'الملخص التنفيذي' },
        { key: 'key', name: 'النقاط الرئيسية' },
        { key: 'decisions', name: 'القرارات المتخذة' },
        { key: 'actions', name: 'المهام والإجراءات' },
        { key: 'next', name: 'الخطوات التالية' },
      ],
    },
    {
      name: 'المحاضرات',
      count: 9,
      items: [{ name: 'أهداف التعلم' }, { name: 'المفاهيم الرئيسية' }],
    },
    {
      name: 'المبيعات',
      count: 11,
      /* "Pain points" is an English idiom, and §1 bans mirroring one in either
         direction — «نقاط الألم» is the calque. «التحديات» names the thing. */
      items: [{ name: 'التحديات' }, { name: 'الاعتراضات' }],
    },
  ],
};

/** Interviews (4) and Projects (4), summarised rather than listed. */
export const HIDDEN_CATEGORIES = 2;
export const CATALOG_TOTAL = 52;
export const CATALOG_CATEGORIES = CATALOG.en.length + HIDDEN_CATEGORIES;

export interface TemplateSection {
  key: SectionKey;
  format: Format;
}

/* EIGHT, and the number is load-bearing twice over: it is what the composer's
   column can hold above the fold at 560px of frame, and it is the count the new
   card reports when it lands back in the grid. The two must agree or the beat
   contradicts itself.

   Locale-NEUTRAL: the order and the formats are the template's shape, which does
   not change with language. Only the labels below do. */
export const SECTIONS: readonly TemplateSection[] = [
  { key: 'title', format: 'paragraph' },
  { key: 'date', format: 'paragraph' },
  { key: 'attendees', format: 'table' },
  { key: 'exec', format: 'paragraph' },
  { key: 'key', format: 'bullet' },
  { key: 'decisions', format: 'numbered' },
  { key: 'actions', format: 'table' },
  { key: 'next', format: 'numbered' },
];

export const SECTION_LABEL: Record<Locale, Record<SectionKey, string>> = {
  en: {
    title: 'Meeting Title',
    date: 'Meeting Date',
    attendees: 'Attendees',
    exec: 'Executive Summary',
    key: 'Key Points',
    decisions: 'Decisions Made',
    actions: 'Action Items',
    next: 'Next Steps',
  },
  ar: {
    title: 'عنوان الاجتماع',
    date: 'تاريخ الاجتماع',
    attendees: 'الحضور',
    exec: 'الملخص التنفيذي',
    key: 'النقاط الرئيسية',
    decisions: 'القرارات المتخذة',
    actions: 'المهام والإجراءات',
    next: 'الخطوات التالية',
  },
};

/** The app's own vocabulary for how a section renders. */
export const FORMAT_LABEL: Record<Locale, Record<Format, string>> = {
  en: { paragraph: 'paragraph', bullet: 'bullet', numbered: 'numbered', table: 'table' },
  ar: { paragraph: 'فقرة', bullet: 'نقاط', numbered: 'ترقيم', table: 'جدول' },
};

/* The pill on a system card names its CATEGORY rather than saying "SYSTEM"
   (owner, 2026-09-07). "System" was a true label that told a visitor nothing —
   every card in that group is a system one, and the group heading already says
   so — whereas the category says which meeting the template is FOR, which is
   the question someone scanning a library is actually asking.

   User cards keep "USER" for the opposite reason: there is exactly one thing
   worth knowing about a card in that group, and it is that you made it. */
export type Category =
  | 'General'
  | 'Project Management'
  | 'Sales'
  | 'Educational'
  | 'Legal'
  | 'People';

/* The pill's `data-cat` takes the KEY above and its text comes from here. They
   were one string doing both jobs, which would have dropped every Arabic pill
   back to the default tone the moment the labels translated. */
export const CATEGORY_LABEL: Record<Locale, Record<Category, string>> = {
  en: {
    General: 'General',
    'Project Management': 'Project Management',
    Sales: 'Sales',
    Educational: 'Educational',
    Legal: 'Legal',
    People: 'People',
  },
  ar: {
    General: 'عام',
    'Project Management': 'إدارة المشاريع',
    Sales: 'المبيعات',
    Educational: 'تعليمي',
    Legal: 'قانوني',
    People: 'الأفراد',
  },
};

export interface TemplateCard {
  name: string;
  origin: 'SYSTEM' | 'USER';
  /** System cards only — a user template's pill is its origin. */
  category?: Category;
  sections: number;
}

/* SEVEN SYSTEM TEMPLATES, SPREAD ACROSS WHO ACTUALLY TAKES MINUTES (owner:
   "varying for many user types, Project Manager, Executives, Sales, Legal,
   Students, Minutes Takers"). One card each, named so the persona is legible
   from the title alone — a visitor should recognise their own meeting in this
   grid without a caption explaining the grid.

   THE FOUR USER ONES ARE DELIBERATELY NARROWER than any system template, and
   that contrast is the point rather than decoration: a custom template that
   looked like a stock one would prove nothing about customisation. Each is
   scoped to one recurring meeting, one quarter, or one person's own work — the
   shape only its author would ever build — and their five-to-nine sections
   against the system templates' seven-to-twelve say the same thing
   numerically. "Northgate" is the project tab 4's filter selects, so the two
   tabs read as one workspace rather than two screenshots.

   The two groups are rendered under their own headings and separated by a
   hairline (owner) rather than being sorted into one run — which is what lets
   the system pills drop the word "system" and carry a category instead. */
export const SYSTEM_CARDS: Record<Locale, readonly TemplateCard[]> = {
  en: [
    { name: 'Detailed Meeting Minutes', origin: 'SYSTEM', category: 'General', sections: 12 },
    { name: 'Executive Summary Minutes', origin: 'SYSTEM', category: 'General', sections: 11 },
    { name: 'Project Status Review', origin: 'SYSTEM', category: 'Project Management', sections: 10 },
    { name: 'Sales Call Summary', origin: 'SYSTEM', category: 'Sales', sections: 9 },
    { name: 'Legal Consultation Record', origin: 'SYSTEM', category: 'Legal', sections: 10 },
    { name: 'Lecture Notes', origin: 'SYSTEM', category: 'Educational', sections: 8 },
    { name: 'Interview Debrief', origin: 'SYSTEM', category: 'People', sections: 7 },
  ],
  ar: [
    { name: 'محضر اجتماع مفصل', origin: 'SYSTEM', category: 'General', sections: 12 },
    { name: 'محضر بملخص تنفيذي', origin: 'SYSTEM', category: 'General', sections: 11 },
    { name: 'مراجعة حالة المشروع', origin: 'SYSTEM', category: 'Project Management', sections: 10 },
    { name: 'ملخص مكالمة مبيعات', origin: 'SYSTEM', category: 'Sales', sections: 9 },
    { name: 'سجل استشارة قانونية', origin: 'SYSTEM', category: 'Legal', sections: 10 },
    { name: 'ملاحظات محاضرة', origin: 'SYSTEM', category: 'Educational', sections: 8 },
    { name: 'تقرير مقابلة', origin: 'SYSTEM', category: 'People', sections: 7 },
  ],
};

export const USER_CARDS: Record<Locale, readonly TemplateCard[]> = {
  en: [
    { name: 'Weekly Standup · Northgate', origin: 'USER', sections: 6 },
    { name: 'Client Discovery Call', origin: 'USER', sections: 9 },
    { name: 'Vendor Review · Q3', origin: 'USER', sections: 7 },
    { name: 'Thesis Supervision Notes', origin: 'USER', sections: 5 },
  ],
  ar: [
    { name: 'اجتماع أسبوعي · نورثجيت', origin: 'USER', sections: 6 },
    { name: 'مكالمة استكشاف العميل', origin: 'USER', sections: 9 },
    { name: 'مراجعة الموردين · الربع 3', origin: 'USER', sections: 7 },
    { name: 'ملاحظات الإشراف على الرسالة', origin: 'USER', sections: 5 },
  ],
};

/* Both locales carry the same eleven cards with the same section counts, so one
   total serves both — the counter, the cap and the new card's arithmetic all
   read from here. */
export const TEMPLATE_TOTAL = SYSTEM_CARDS.en.length + USER_CARDS.en.length;

/* The card the cursor picks in the first beat. Deliberately the RICHEST one:
   its twelve sections are what makes "a ready-made template" mean something
   before the composer shows you can build your own. Its detail list is the one
   surface here that scrolls by design — see the rail's note in the CSS. */
export const PICKED = 0;

/* The sections the picked template shows, in the app's own order — which is
   alphabetical BY THE ENGLISH NAME, since that is the order the app stores.
   The AR list keeps that same order rather than re-sorting into Arabic
   alphabetical: the two locales are one template seen twice, and a reader
   comparing them should find row 7 saying the same thing in both. */
export const PICKED_SECTIONS: Record<Locale, readonly string[]> = {
  en: [
    'Action Items',
    'Attendees',
    'Decisions Made',
    'Executive Summary',
    'Key Points',
    'Global Meeting-Notes System Instructions',
    'Meeting Date',
    'Meeting Title',
    'Meeting Type',
    'Next Meeting',
    'Next Steps',
    'Risks & Issues',
  ],
  ar: [
    'المهام والإجراءات',
    'الحضور',
    'القرارات المتخذة',
    'الملخص التنفيذي',
    'النقاط الرئيسية',
    'تعليمات النظام العامة لملاحظات الاجتماعات',
    'تاريخ الاجتماع',
    'عنوان الاجتماع',
    'نوع الاجتماع',
    'الاجتماع التالي',
    'الخطوات التالية',
    'المخاطر والمشكلات',
  ],
};

/** The one the composer builds, which lands in the grid's empty ninth cell. */
export const NEW_CARD: Record<Locale, TemplateCard> = {
  en: { name: 'Board Meeting Minutes', origin: 'USER', sections: SECTIONS.length },
  ar: { name: 'محضر اجتماع مجلس الإدارة', origin: 'USER', sections: SECTIONS.length },
};

export const TEMPLATE_CAP = 30;

export type Phase = 'browse' | 'inspect' | 'compose' | 'saved';

/**
 * What the scripted cursor is reaching for. `item:<key>` is one catalog row.
 *
 * The rows used to tick themselves while the dot was hidden — a deliberate
 * choice at the time ("nothing is being pressed, and a cursor hovering over a
 * list that fills itself claims a gesture that is not happening"). The owner
 * overruled it on 2026-09-07: with no cursor there, a visitor cannot see that
 * the sections are being CHOSEN, and the composer's whole point is that you
 * pick them. So the dot now presses each row, and the section lands on the
 * release — the same cause-then-effect ordering every other beat uses.
 */
export type Aim = 'card' | 'new' | 'publish' | `item:${SectionKey}` | null;

/* Timing. FOUR beats now, because the owner asked for a step before the
   composer: the cursor should "pick one of the templates, see its content, then
   decide to create a custom template".

   That order is the argument the tab is making. Browsing first says the library
   arrives stocked; opening one says a template is a real, inspectable thing;
   only then does building your own read as a choice rather than a chore.

   0.7s a section, up from 0.6 (owner, 2026-09-07): each row is now PRESSED by
   the cursor rather than ticking itself, so the step has to hold a whole
   aim → travel → press → release, not just a state change.

   The 100ms is the entire cost of the change, and it is affordable because
   TouchDot's travel is now distance-aware: consecutive catalog rows are ~26px
   apart, which is the 180ms floor rather than the old flat 780ms. Budget per
   step: aim at 0, arrive by ~180, press at 300, release at 550, 150ms of air.

   Tab 4's first cut fired every 0.84s and the owner read it as frantic — but
   that was six presses across the whole shell with three things moving at once.
   Here the dot steps down one short list and one row lands at a time.

   Press beats are 800ms after the aim on the LONG legs (card, New, publish), so
   the dot arrives before it presses — the correction tab 4 needed when its
   script fired mid-travel. */
const T = {
  aimCard: 700,
  pressCard: 1500,
  aimNew: 4300,
  pressNew: 5100,
  first: 5700,
  step: 700,
  /** Aim-to-press inside the catalog, where the legs are short. */
  itemPress: 300,
  publishLit: 300,
  aimPublish: 700,
  pressPublish: 800,
  close: 700,
} as const;

/** How long the dot's press animation reads for. Matches tab 4's.
    Declared BEFORE the derived offsets below, which now include it — a
    module-level const referenced above its own declaration is a TDZ throw at
    import time, not a hoisted undefined. */
const PRESS_MS = 250;

/* When the LAST section lands, which is now the end of a press rather than the
   moment a timer fires: aim, then itemPress, then the press itself. Everything
   after it hangs off this, so getting it wrong would light Save & Publish
   before the template was complete. */
const LAST_SECTION =
  T.first + (SECTIONS.length - 1) * T.step + T.itemPress + PRESS_MS;
const PUBLISH_LIT_AT = LAST_SECTION + T.publishLit;
const AIM_PUBLISH_AT = PUBLISH_LIT_AT + T.aimPublish;
const PRESS_PUBLISH_AT = AIM_PUBLISH_AT + T.pressPublish;
const CLOSE_AT = PRESS_PUBLISH_AT + T.close;

export interface LibraryDemo {
  phase: Phase;
  /** How many sections have landed. Also drives the catalog rail's ticks. */
  placed: number;
  /** Save & Publish has gone primary — the template is complete. */
  publishable: boolean;
  aim: Aim;
  dotVisible: boolean;
  pressing: boolean;
}

export function useLibraryDemo(): LibraryDemo {
  /* Read once, at mount, rather than subscribing — the sequence is scheduled in
     one shot below and a mid-flight preference change has nothing to re-drive.
     Guarded for SSR: this hook runs inside the prerender.

     Reduced motion gets the END state, not a frozen first one, matching
     useRecorderDemo. That is the stocked library WITH the new card in it, which
     is the frame that says the most on its own — and no dot, because a cursor
     frozen mid-gesture is a claim about motion that is not happening. */
  const still = useRef(
    typeof window !== 'undefined' &&
      Boolean(window.matchMedia?.('(prefers-reduced-motion: reduce)').matches)
  ).current;

  const [phase, setPhase] = useState<Phase>(still ? 'saved' : 'browse');
  const [placed, setPlaced] = useState(still ? SECTIONS.length : 0);
  const [publishable, setPublishable] = useState(still);
  const [aim, setAim] = useState<Aim>(null);
  const [dotVisible, setDotVisible] = useState(false);
  const [pressing, setPressing] = useState(false);

  useEffect(() => {
    if (still) return;
    const press = (at: number, then: () => void) => [
      window.setTimeout(() => setPressing(true), at),
      window.setTimeout(() => {
        setPressing(false);
        then();
      }, at + PRESS_MS),
    ];

    const timers = [
      // Beat 1 — pick a template and read it.
      window.setTimeout(() => {
        setAim('card');
        setDotVisible(true);
      }, T.aimCard),
      ...press(T.pressCard, () => setPhase('inspect')),

      // Beat 2 — decide to build one instead.
      window.setTimeout(() => setAim('new'), T.aimNew),
      ...press(T.pressNew, () => setPhase('compose')),

      /* Beat 3 — the cursor picks the sections, one catalog row at a time.
         The dot STAYS VISIBLE through this (owner, 2026-09-07). It used to hide
         here, on the reasoning that a cursor over a self-filling list claims a
         gesture that is not happening — but the gesture IS the point: the
         composer exists to show that you choose what a template contains, and
         with no cursor the rows just ticked themselves and the choosing was
         invisible. Each row is aimed, pressed, and lands on the release. */
      ...SECTIONS.flatMap((s, i) => {
        const at = T.first + i * T.step;
        return [
          window.setTimeout(() => setAim(`item:${s.key}`), at),
          ...press(at + T.itemPress, () => setPlaced(i + 1)),
        ];
      }),
      window.setTimeout(() => setPublishable(true), PUBLISH_LIT_AT),

      // Beat 4 — publish, and watch it land in the library.
      window.setTimeout(() => {
        setAim('publish');
        setDotVisible(true);
      }, AIM_PUBLISH_AT),
      ...press(PRESS_PUBLISH_AT, () => undefined),
      window.setTimeout(() => {
        setPhase('saved');
        setDotVisible(false);
        setAim(null);
      }, CLOSE_AT),
    ];
    return () => timers.forEach((id) => window.clearTimeout(id));
  }, [still]);

  return useMemo(
    () => ({ phase, placed, publishable, aim, dotVisible, pressing }),
    [phase, placed, publishable, aim, dotVisible, pressing]
  );
}

/* Context for the same reason the recorder and the actions page have one: the
   sequence drives two surfaces that live in different subtrees — the page, which
   is the shell's `children`, and the dialog, which is its `overlay` so it can
   scrim the sidebar and top bar the way the app's modal does. One page state,
   two places that read it. */
const LibraryDemoContext = createContext<LibraryDemo | null>(null);

export const LibraryDemoProvider = LibraryDemoContext.Provider;

export function useLibraryDemoState(): LibraryDemo | null {
  return useContext(LibraryDemoContext);
}
